import type { OvpClient } from '@bldsoft/cappie/api/ovp'
import type {
  ForgotPasswordOutputDto,
  QrGetCodeInfoResponseDto,
  SignInOutputDto,
  SignUpOutputDto,
} from '@bldsoft/ovp-api-types'
import type { API } from '@setplex/tria-api'
import { body, payload } from '../../../result'
import { formatSignInResponse } from './format'

export function use(client: OvpClient, user: API['user'], _api: API): void {
  // POST /api/web/auth/signin
  user.signInFx.use(async ({ email, password, rememberMe }) => {
    const data = payload<SignInOutputDto>(
      await client.post.api.web.auth.signin({
        body: { username: email, password, rememberMe },
      })
    )

    if (!data) {
      throw new Error('Empty answer in user.signInFx')
    }

    return formatSignInResponse(data)
  })

  // GET /api/web/auth/logout
  user.logOutFx.use(async () => {
    payload(await client.get.api.web.auth.logout())
  })

  // GET /api/web/auth/activate?token=${token}
  user.activateTokenFx.use(async ({ token }) => {
    payload(await client.get.api.web.auth.activate({ query: { token } }))
  })

  // POST /api/web/auth/signup
  user.signUpFx.use(
    async ({
      email,
      password,
      firstName,
      lastName,
      birthDate,
      gender,
      marketingConsent = true,
    }) => {
      const data = payload<SignUpOutputDto>(
        await client.post.api.web.auth.signup({
          body: {
            email,
            password,
            firstName,
            lastName,
            birthDate,
            gender,
            marketingConsent,
          },
        })
      )

      if (!data) {
        throw new Error('Empty answer in user.signUpFx')
      }

      return data
    }
  )

  // Forgot password
  // POST /api/web/auth/recovery
  user.resetPasswordFx.use(async ({ email }) => {
    const data = payload<ForgotPasswordOutputDto>(
      await client.post.api.web.auth.recovery({ body: { email } })
    )

    if (!data) {
      throw new Error('Empty answer in user.resetPasswordFx')
    }

    return data
  })

  // GET /api/web/auth/forgot/${token}
  user.confirmResetPasswordFx.use(async ({ token }) => {
    const data = payload<ForgotPasswordOutputDto>(
      await client.get.api.web.auth.forgot(token)
    )

    if (!data) {
      throw new Error('Empty answer in user.confirmResetPasswordFx')
    }

    return data
  })

  // POST /api/web/auth/forgot
  user.setPasswordFx.use(async ({ password, token }) => {
    payload(
      await client.post.api.web.auth.forgot({ body: { password, token } })
    )
  })

  // GET api/web/auth/qr/info?token={token}
  user.getInfoViaQRCodeFx.use(async ({ token }) => {
    const data = payload<QrGetCodeInfoResponseDto>(
      await client.get.api.web.auth.qr.info({ query: { token } })
    )

    if (!data) {
      throw new Error('Empty answer in user.getInfoViaQRCodeFx')
    }

    return data
  })

  // POST api/web/auth/qr/signIn?token={token}
  user.confirmSignInByQRCodeFx.use(async ({ token }) => {
    const json = body(
      await client.post.api.web.auth.qr.signin({ query: { token } })
    )
    if (!json) {
      throw new Error('Empty answer in user.confirmSignInByQRCodeFx')
    }

    return json
  })

  // POST /api/web/creators/join
  user.creatorJoinFx.use(
    async ({ name, email, phone, country, company, numberOfFilms }) => {
      const data = payload<ForgotPasswordOutputDto>(
        await client.post.api.web.creators.join({
          body: { name, email, phone, country, company, numberOfFilms },
        })
      )

      if (!data) {
        throw new Error('Empty answer in user.creatorSignUpFx')
      }

      return data
    }
  )
}
